import React, { useState, useEffect } from 'react';
import { Globe, Check, MapPin } from 'lucide-react';
import { TIMEZONES } from '../../utils/timezone';
import { useDoctorData } from '../../context/DoctorDataContext';

const TimezoneSelector: React.FC = () => {
  const { doctorTimezone, updateTimezone } = useDoctorData();
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detectedTimezone, setDetectedTimezone] = useState<string | null>(null);

  // Detect browser timezone on mount
  useEffect(() => {
    try {
      const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      setDetectedTimezone(browserTz);
      console.log('Detected browser timezone:', browserTz);
    } catch (err) {
      console.error('Failed to detect timezone:', err);
    }
  }, []);

  const handleTimezoneChange = async (newTimezone: string) => {
    try {
      setSaving(true);
      setError(null);
      
      await updateTimezone(newTimezone);
      
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError('Failed to update timezone. Please try again.');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleUseDetected = () => {
    if (detectedTimezone) {
      // Check if detected timezone is in our list
      const matchingTz = TIMEZONES.find(tz => tz.value === detectedTimezone);
      if (matchingTz) {
        handleTimezoneChange(detectedTimezone);
      } else {
        // Try to find a close match
        const closeMatch = TIMEZONES.find(tz => 
          detectedTimezone.includes(tz.value.split('/')[1]) || 
          tz.value.includes(detectedTimezone.split('/')[1])
        );
        if (closeMatch) {
          handleTimezoneChange(closeMatch.value);
        } else {
          setError('Your detected timezone is not in our supported list. Please select manually.');
        }
      }
    }
  };

  return (
    <div className="card">
      <div className="flex items-center space-x-3 mb-4">
        <Globe className="w-6 h-6 text-blue-600" />
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Timezone Settings</h3>
          <p className="text-sm text-gray-600">
            All consultation times will be displayed in your selected timezone
          </p>
        </div>
      </div>

      {/* Detected Timezone Banner */}
      {detectedTimezone && detectedTimezone !== doctorTimezone && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start space-x-2">
            <MapPin className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-blue-900 font-medium">
                We detected your location timezone: {detectedTimezone}
              </p>
              <button
                onClick={handleUseDetected}
                disabled={saving}
                className="mt-2 text-sm text-blue-600 hover:text-blue-800 font-medium underline disabled:opacity-50"
              >
                Use this timezone
              </button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm flex items-center space-x-2">
          <Check className="w-4 h-4" />
          <span>Timezone updated successfully!</span>
        </div>
      )}

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select Your Timezone
        </label>
        <select
          value={doctorTimezone}
          onChange={(e) => handleTimezoneChange(e.target.value)}
          disabled={saving}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {TIMEZONES.map((tz) => (
            <option key={tz.value} value={tz.value}>
              {tz.label}
            </option>
          ))}
        </select>
        
        {saving && (
          <p className="text-sm text-gray-500 mt-2">Saving...</p>
        )}
      </div>

      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800">
          <strong>Current timezone:</strong> {doctorTimezone}
        </p>
        <p className="text-xs text-blue-600 mt-1">
          Patient times will be automatically converted to your timezone for easy scheduling
        </p>
      </div>
    </div>
  );
};

export default TimezoneSelector;
